import { TextField } from '@material-ui/core';
import Button from 'bz-core-controls/controls/general/button';
import withDisplayName from 'bz-utilities/hoc/display-name';
import useChangeEffect from 'lib/hooks/useChangeEffect';
import { isObservableArray } from 'mobx';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ActivityItem from './controls/create/controls/activity-item';

interface IHasId {
  id: number;
}

interface IHasIdMarker {
  caseId: number;
}

export class Psfs implements IHasId, IHasIdMarker {
  public id: number;
  public caseId: number;
  public activity: string;
  public linkedGoalId: number;
  public initialValue: number;

  public constructor(init?: Partial<Psfs>) {
    Object.assign(this, init);
    this.initialValue = init?.initialValue || 0;
  }
}

export interface IPSFSProps {
  onChange?: (activities: Psfs[]) => void;
  value?: Psfs[];
  psfs?: Psfs[];
  readOnly?: boolean;
}

function convertToPsfsArray(
  inputObj: Record<string, { psfsLabel: string; psfsValue: number }>
): Psfs[] {
  return Object.entries(inputObj).map(([key, value]) => ({
    id: parseInt(key),
    activity: value.psfsLabel,
    initialValue: value.psfsValue,
    caseId: 0,
    linkedGoalId: 0
  }));
}

const ConnectPsfsComponent: React.FC<IPSFSProps> = withDisplayName((props) => {
  const { readOnly, psfs, onChange, value } = props;

  const [activityName, setActivityName] = useState('');
  const [existingActivityError, setExistingActivityError] = useState(false);

  const [activities, setActivities] = useState<Psfs[]>(() => {
    const merged: Psfs[] = [];
    let convertedArray: Psfs[] = [];

    // due to some legacy code, value can be an object or an array. If it's an object we convert it
    if (!Array.isArray(value) && !isObservableArray(value) && value != null) {
      convertedArray = convertToPsfsArray(value);
    }

    function findMatch(arr: Psfs[], activity: string): Psfs | undefined {
      const lActivity = activity.toLowerCase();
      return arr?.find((item) => item.activity.toLowerCase() === lActivity);
    }

    let valueCopy: Psfs[] = [];

    if (convertedArray.length > 0) {
      valueCopy = convertedArray;
    } else {
      valueCopy = value ? [...value] : [];
    }

    // psfs[] contains any psfs against the case. Clash the value array with the psfs to prevent duplicate activities being added
    psfs?.forEach((psfsItem) => {
      const match = findMatch(valueCopy, psfsItem.activity);

      if (match) {
        merged.push({
          ...psfsItem,
          initialValue: match.initialValue
        });

        valueCopy = valueCopy.filter((item) => item.activity !== match.activity);
      } else {
        merged.push(psfsItem);
      }
    });

    valueCopy.forEach((valueItem) => {
      merged.push({ ...valueItem, linkedGoalId: 0 });
    });

    return merged;
  });

  useEffect(() => {
    if (!psfs) {
      setExistingActivityError(false);
      return;
    }

    const target = activityName.trim().toLowerCase();
    const exists = psfs.some((x) => x.activity.trim().toLowerCase() == target);

    setExistingActivityError(exists);
  }, [activityName, psfs]);

  const maximumActivitiesReached = useMemo(() => {
    return activities.length === 5;
  }, [activities]);

  useChangeEffect(() => {
    onChange?.(activities);
  }, [activities]);

  const addActivity = useCallback(() => {
    const newActivity = new Psfs({
      activity: activityName,
      initialValue: -1, // Start the scale on N/A rather than 1
      linkedGoalId: 0
    });
    setActivities([...activities, newActivity]);
    setActivityName('');
  }, [activityName, activities]);

  const removeActivity = useCallback(
    (activity: string) => {
      setActivities(activities.filter((a) => a.activity !== activity));
    },
    [activities]
  );

  const canBeRemoved = useCallback(
    (activity: string) => {
      const target = activity.toLowerCase();
      if (psfs) {
        return !psfs.some((x) => x.activity.toLowerCase() == target);
      }
      return true;
    },
    [psfs]
  );

  const updateInitialValue = useCallback(
    (value: number, activity: string) => {
      setActivities(
        activities.map((a) => (a.activity === activity ? { ...a, initialValue: value } : a))
      );
    },
    [activities]
  );

  return (
    <div className="mx-1 my-2">
      {!readOnly && (
        <div className="mb-4 flex items-center gap-2">
          <TextField
            id="outlined-basic"
            label="Activity"
            disabled={readOnly}
            value={activityName}
            onChange={(e) => setActivityName(e.target.value)}
            placeholder="Enter activity name"
            className="mr-2 w-full border p-2"
          />
          <Button
            disabled={maximumActivitiesReached || existingActivityError || readOnly}
            onClick={addActivity}
            className="bg-blue-500 px-4 py-2 text-white"
          >
            Add
          </Button>
        </div>
      )}
      {existingActivityError && <div className="text-red-500">Activity already exists</div>}
      {maximumActivitiesReached && (
        <div className="text-red-500">Maximum amount of PSFS activities reached.</div>
      )}
      <ul>
        {activities.map((a, index) => {
          const isRemovable = canBeRemoved(a.activity);

          return (
            <ActivityItem
              key={index}
              removable={isRemovable}
              activity={a}
              readOnly={readOnly}
              removeActivity={removeActivity}
              updateInitialValue={updateInitialValue}
            />
          );
        })}
      </ul>
    </div>
  );
}, 'ConnectPsfsComponent');

export default ConnectPsfsComponent;
